<template>
  <div class="custom-datepicker" :class="{ active: isOpen }">
    <label v-if="title" class="label">{{ title }}</label>
    <div class="datepicker" ref="customDatepicker" v-click-outside="handleClosePopup" @click="onClick">
      <span class="date">{{ formattedDate }}</span>
      <slot name="icon">
        <v-icon class="datepicker-icon">
          calendar_today
        </v-icon>
      </slot>
    </div>
    <datepicker
      :picker-options='pickerOptions'
      v-model="selectedDate"
      :disabled='disabled'
      :type="type"
      ref="datepicker"
      :popper-class="`date-picker-section ${ Object.keys(pickerOptions).length > 0 ? 'topBar' : ''}`"
      class="original-picker"
      @blur="onBlur"
    />
  </div>
</template>

<script>
import { DatePicker } from 'element-ui';
import vClickOutside from 'v-click-outside';
import { formatSingleDateToDatepicker, formatRangeDateToDatepicker } from '@/utils/dateUtils';

export default {
  name: 'CustomDatepicker',
  components: {
    datepicker: DatePicker,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  data() {
    return {
      isOpen: false,
    };
  },
  props: {
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    pickerOptions: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    value: {
      type: [String, Date, Array],
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    title: {
      type: String,
    },
    format: {
      type: String,
    },
  },
  computed: {
    selectedDate: {
      get() {
        return this.value;
      },
      set(value) {
        if (!this.disabled) {
          this.$emit('input', value);
        }
      },
    },
    formattedDate() {
      if (this.value === '') {
        return formatSingleDateToDatepicker(Date.now(), this.format);
      }
      if (this.type === 'monthrange') {
        return formatRangeDateToDatepicker(this.value, this.type);
      } if (this.type === 'daterange') {
        return formatRangeDateToDatepicker(this.value, this.type);
      }
      return formatSingleDateToDatepicker(this.value, this.format);
    },
  },
  methods: {
    onClick() {
      if (!this.isOpen) {
        this.isOpen = true;
        return this.$refs.datepicker.focus();
      }
      return this.handleClosePopup();
    },
    onBlur() {
      this.handleClosePopup();
    },
    handleClosePopup() {
      this.isOpen = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/design-system';

.custom-datepicker {
  flex: 1;
  position: relative;
}

.datepicker {
  align-items: center;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: $radius-sm;
  cursor: pointer;
  display: flex;
  height: rem(28);
  justify-content: space-between;
  min-height: rem(40);
  max-height: rem(40);
  padding: $space-2;
  width: 100%;

  .date {
    color: var(--text-primary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    white-space: nowrap;
    padding-top: $space-1;
  }

  &-icon {
    margin-left: $space-1;
    font-size: $font-size-md;
    color: var(--text-muted);
  }
}

.custom-datepicker.active .datepicker {
  border-color: var(--text-muted);
}

.original-picker {
  opacity: 0;
  height: 0;
  overflow: hidden;
  position: absolute;
  bottom: 0;

  :deep(input) {
    height: 1px;
  }

  :deep(span) {
    display: none;
  }
}

label {
  display: block;
  color: var(--text-secondary);
  font-family: $font-family-regular;
  font-size: $font-size-xs;
  letter-spacing: 0.8px;
  line-height: $font-size-xs;
  margin-bottom: $space-1;
  text-transform: uppercase;
}
</style>

<style lang="scss">
@import '../../assets/styles/design-system';

.el-picker-panel {
  &__content {
    .el-month-table {
      font-family: $font-family-regular;
      font-size: $font-size-base;
      line-height: rem(18);
    }
    .el-date-range-picker {
      &__header div {
        font-family: $font-family-regular;
        font-size: $font-size-lg;
        line-height: rem(24);
      }
    }
  }
}

.el-picker-panel {
  &.date-picker-section {
    .el-picker-panel__sidebar > :last-child {
      border-radius: 0 $radius-md $radius-md 0;
    }

    .el-picker-panel__sidebar > :first-child {
      border-radius: $radius-md 0 0 $radius-md;
    }
    &.topBar {
      .el-picker-panel__body-wrapper {
        display: flex;
        flex-direction: column;
        .el-picker-panel__body {
          margin-left: 0;
        }
        .el-picker-panel__sidebar {
          width: 100%;
          display: flex;
          position: initial;
          margin-top: $space-4;
          margin-bottom: $space-2;
          padding-left: rem(80);
          padding-right: rem(80);
          .el-picker-panel__shortcut {
            border: 1px solid var(--border-primary);
            background-color: var(--bg-secondary);
            text-align: center;
            font-family: $font-family-bold;
            font-size: $font-size-base;
            line-height: rem(24);
            &:hover {
              color: var(--brand-primary);
            }
          }
        }
      }
    }
  }
}
</style>
